/**
 * Vetting Promotion — Phase 1b (patched)
 * ──────────────────────────────────────
 * Single writer for `Contractor.vettingStatus`.
 *
 *   • markVettingStep()    — records evidence (license / insurance /
 *                            photos / skill checklist). Pure, idempotent.
 *   • promoteToApproved()  — HARD GATE. Refuses unless ALL of the
 *                            following are satisfied:
 *                              1. license verified
 *                              2. insurance verified
 *                              3. ≥ 6 submitted job photos
 *                              4. skill checklist completed
 *                              5. jobsCompleted ≥ 6
 *                              6. executionScore ≥ 75 (non-null)
 *                              7. manual approver + reason (≥10 chars,
 *                                 approver ≠ contractor.id)
 *
 * `vettingStatus = "blocked"` is set ONLY by admin override (Phase 1c
 * surface). This module never auto-blocks.
 *
 * Anchor: mem://architecture/network-execution-phase-1b
 */

import type { Contractor, Job } from "@/data/types";
import type { Callback } from "./callback";
import { computeExecutionScore } from "./executionScore";

/** Locked promotion thresholds — see RECONCILIATION.md §3 + §4. */
export const VETTING_PROMOTION_REQUIREMENTS = {
  minPhotos: 6,
  minJobsCompleted: 6,
  minScore: 75,
  minReasonLength: 10,
} as const;

export type VettingStep =
  | "license"
  | "insurance"
  | "photo"
  | "skillChecklist";

export interface MarkVettingStepInput {
  contractor: Contractor;
  step: VettingStep;
  /** For "photo" only — appended to submittedJobPhotos. */
  photo?: { url: string; jobType: string };
  nowIso?: string;
}

function ensureVetting(c: Contractor): NonNullable<Contractor["vetting"]> {
  return (
    c.vetting ?? {
      licenseVerified: false,
      insuranceVerified: false,
      submittedJobPhotos: [],
      skillChecklistCompleted: false,
    }
  );
}

export function markVettingStep(input: MarkVettingStepInput): {
  contractor: Contractor;
  changed: boolean;
} {
  const { contractor, step, photo } = input;
  const now = input.nowIso ?? new Date().toISOString();
  const v = ensureVetting(contractor);

  let nextV = v;
  switch (step) {
    case "license":
      if (v.licenseVerified) return { contractor, changed: false };
      nextV = { ...v, licenseVerified: true };
      break;
    case "insurance":
      if (v.insuranceVerified) return { contractor, changed: false };
      nextV = { ...v, insuranceVerified: true };
      break;
    case "photo": {
      if (!photo) return { contractor, changed: false };
      nextV = {
        ...v,
        submittedJobPhotos: [
          ...v.submittedJobPhotos,
          { url: photo.url, jobType: photo.jobType, uploadedAt: now },
        ],
      };
      break;
    }
    case "skillChecklist":
      if (v.skillChecklistCompleted) return { contractor, changed: false };
      nextV = { ...v, skillChecklistCompleted: true };
      break;
  }

  return { contractor: { ...contractor, vetting: nextV }, changed: true };
}

export interface PromoteToApprovedInput {
  contractor: Contractor;
  /** Required for the score-gate computation. */
  jobs: Job[];
  callbacks: Callback[];
  /** Manual approver — never the contractor themselves. Required. */
  approvedBy: string;
  /** Free-text justification, ≥10 chars. Required. */
  reason: string;
  nowIso?: string;
}

export interface PromoteToApprovedResult {
  contractor: Contractor;
  promoted: boolean;
  /** Specific failed gate keys when promotion is refused. */
  blockedBy: string[];
}

/**
 * HARD GATE — refuses promotion unless every condition is met. Idempotent:
 * an already-approved contractor returns { promoted: false, blockedBy: [] }
 * with a no-op state. Records `approvedBy` + `reason` on the vetting
 * evidence object for audit.
 */
export function promoteToApproved(input: PromoteToApprovedInput): PromoteToApprovedResult {
  const { contractor, jobs, callbacks, approvedBy, reason } = input;
  const now = input.nowIso ?? new Date().toISOString();
  const req = VETTING_PROMOTION_REQUIREMENTS;

  if (contractor.vettingStatus === "approved") {
    return { contractor, promoted: false, blockedBy: [] };
  }
  if (contractor.vettingStatus === "blocked") {
    return { contractor, promoted: false, blockedBy: ["vetting_blocked"] };
  }

  const blockedBy: string[] = [];

  // Approver + reason gates (manual-approval requirement).
  const trimmedApprover = (approvedBy ?? "").trim();
  if (!trimmedApprover) blockedBy.push("missing_approver");
  if (trimmedApprover && trimmedApprover === contractor.id) {
    blockedBy.push("self_approval_forbidden");
  }
  const trimmedReason = (reason ?? "").trim();
  if (trimmedReason.length < req.minReasonLength) {
    blockedBy.push("reason_too_short");
  }

  // Evidence gates.
  const v = ensureVetting(contractor);
  if (!v.licenseVerified) blockedBy.push("license_unverified");
  if (!v.insuranceVerified) blockedBy.push("insurance_unverified");
  if (v.submittedJobPhotos.length < req.minPhotos) blockedBy.push("insufficient_photos");
  if (!v.skillChecklistCompleted) blockedBy.push("skill_checklist_incomplete");

  // Track-record gates.
  const completed = contractor.jobsCompleted ?? 0;
  if (completed < req.minJobsCompleted) blockedBy.push("insufficient_jobs_completed");

  const scoreResult = computeExecutionScore({ contractor, jobs, callbacks });
  if (scoreResult.score === null) {
    blockedBy.push("score_unavailable");
  } else if (scoreResult.score < req.minScore) {
    blockedBy.push("score_below_threshold");
  }

  if (blockedBy.length > 0) {
    return { contractor, promoted: false, blockedBy };
  }

  return {
    contractor: {
      ...contractor,
      vettingStatus: "approved",
      vetting: {
        ...v,
        verifiedAt: v.verifiedAt ?? now,
        approvedBy: trimmedApprover,
        approvalReason: trimmedReason,
        approvedAt: now,
      },
    },
    promoted: true,
    blockedBy: [],
  };
}
